/**
 * Visitor Recommendations Section
 * Fetches personalized recommendations from the API and renders them
 */

(function() {
  'use strict';

  const SUPABASE_URL = 'https://bernard-recommender.bernard-legoff-29.workers.dev';

  function init() {
    const sections = document.querySelectorAll('.visitor-recommendations');
    sections.forEach(initSection);
  }

  function initSection(section) {
    const sectionId = section.dataset.sectionId;
    const productLimit = parseInt(section.dataset.productLimit, 10) || 8;
    const columnsDesktop = parseInt(section.dataset.columnsDesktop, 10) || 6;
    const columnsTablet = parseInt(section.dataset.columnsTablet, 10) || 3;

    const loadingEl = section.querySelector('[data-loading]');
    const contentEl = section.querySelector('[data-content]');
    const emptyEl = section.querySelector('[data-empty]');
    const container = section.querySelector('[data-products-container]');

    if (!container) {
      console.error('[VisitorReco] Container not found');
      return;
    }

    loadRecommendations({
      sectionId,
      productLimit,
      columnsDesktop,
      columnsTablet,
      loadingEl,
      contentEl,
      emptyEl,
      container
    });
  }

  async function getVisitorId() {
    // Try to get visitor ID from theme utils
    if (window._rm && typeof window._rm.gv === 'function') {
      try {
        const visitorId = await window._rm.gv();
        if (visitorId) return visitorId;
      } catch (e) {
        console.warn('[VisitorReco] Failed to get visitor ID:', e);
      }
    }

    // Wait a bit and retry
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (window._rm && typeof window._rm.gv === 'function') {
      try {
        return await window._rm.gv();
      } catch (e) {
        console.warn('[VisitorReco] Retry failed:', e);
      }
    }

    return null;
  }

  async function fetchRecommendations(visitorId, limit) {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/recommendations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorId, limit })
    });

    if (!response.ok) {
      throw new Error(`Recommendations API error: ${response.status}`);
    }

    return response.json();
  }

  function filterInStock(products) {
    return (products || []).filter(p => {
      const qty = p.totalInventory;
      if (qty === null || qty === undefined) return true;
      return qty > 0;
    });
  }

  function showLoading(els) {
    if (els.loadingEl) els.loadingEl.style.display = 'flex';
    if (els.contentEl) els.contentEl.style.display = 'none';
    if (els.emptyEl) els.emptyEl.style.display = 'none';
  }

  function showContent(els) {
    if (els.loadingEl) els.loadingEl.style.display = 'none';
    if (els.contentEl) els.contentEl.style.display = 'block';
    if (els.emptyEl) els.emptyEl.style.display = 'none';
  }

  function showEmpty(els) {
    if (els.loadingEl) els.loadingEl.style.display = 'none';
    if (els.contentEl) els.contentEl.style.display = 'none';
    if (els.emptyEl) els.emptyEl.style.display = 'block';
  }

  async function loadRecommendations(config) {
    const { productLimit, container } = config;

    showLoading(config);

    try {
      const visitorId = await getVisitorId();

      if (!visitorId) {
        console.log('[VisitorReco] No visitor ID, showing empty state');
        showEmpty(config);
        return;
      }

      console.log('[VisitorReco] Fetching recommendations for:', visitorId);

      const data = await fetchRecommendations(visitorId, productLimit);

      if (!data?.products || data.products.length === 0) {
        console.log('[VisitorReco] No recommendations available');
        showEmpty(config);
        return;
      }

      data.products = filterInStock(data.products);

      if (data.products.length === 0) {
        console.log('[VisitorReco] All recommendations out of stock');
        showEmpty(config);
        return;
      }

      console.log('[VisitorReco] Received', data.products.length, 'products');

      // Render products using ProductCardDynamic
      if (window.ProductCardDynamic) {
        window.ProductCardDynamic.renderGrid(container, data.products, {
          impressionContext: 'visitor-recommendations',
          enableHoverImages: true,
          hoverImagesCount: 2,
          showFeedback: false
        });

        // Wrap each card in carousel__slide
        const cards = container.querySelectorAll('product-card');
        cards.forEach(card => {
          const slide = document.createElement('div');
          slide.className = 'carousel__slide';
          card.parentNode.insertBefore(slide, card);
          slide.appendChild(card);
        });
      }

      showContent(config);
      setupImpressionTracking(container);

      // Re-initialize theme-scroll for arrow navigation
      const themeScroll = container.closest('theme-scroll');
      if (themeScroll && typeof themeScroll.init === 'function') {
        themeScroll.init();
      }

    } catch (error) {
      console.error('[VisitorReco] Error loading recommendations:', error);
      showEmpty(config);
    }
  }

  function setupImpressionTracking(container) {
    const productCards = container.querySelectorAll('[data-product-gid]');

    if (productCards.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          const gid = el.dataset.productGid;
          const context = el.dataset.impressionContext || 'visitor-recommendations';

          if (gid && window._rm && typeof window._rm.seen === 'function') {
            const seen = window._rm.seen();
            if (!seen[context]) {
              seen[context] = new Set();
            }
            seen[context].add(gid);
          }

          observer.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    productCards.forEach(card => observer.observe(card));
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
